import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Venus, Mars } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const OPTIONS = [
  { value: "female", label: "Madam", icon: Venus, on: "bg-pink-500 border-pink-500 text-white", cls: "hover:bg-pink-50" },
  { value: "male", label: "Sir", icon: Mars, on: "bg-sky-500 border-sky-500 text-white", cls: "hover:bg-sky-50" },
];

export default function GenderBulkDialog({ open, setOpen, teachers, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const staff = (teachers || []).filter((t) => t.role !== "admin");

  useEffect(() => {
    if (open) {
      const init = {};
      staff.forEach((t) => { init[t.id] = t.gender || ""; });
      setDraft(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setOne = (id, gender) => setDraft((d) => ({ ...d, [id]: d[id] === gender ? "" : gender }));
  const setAll = (gender) => {
    const next = {};
    staff.forEach((t) => { next[t.id] = gender; });
    setDraft(next);
  };

  const pending = staff.filter((t) => (draft[t.id] || "") !== (t.gender || ""));

  const save = async () => {
    if (!pending.length) { setOpen(false); return; }
    setSaving(true);
    try {
      const updates = pending.map((t) => ({ id: t.id, gender: draft[t.id] || "" }));
      const { data } = await api.post("/admin/teachers/gender-bulk", { updates });
      toast.success(`Updated gender for ${data.updated} teacher${data.updated === 1 ? "" : "s"}`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const unset = staff.filter((t) => !(draft[t.id] || "")).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="gender-bulk-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Set teacher gender</DialogTitle>
          <DialogDescription>
            Used for the "Madam / Sir" title in report-card WhatsApp messages. Female → Madam, Male → Sir.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 py-1">
          <span className="text-xs text-slate-500" data-testid="gender-bulk-unset-count">
            {unset} of {staff.length} still unset
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setAll("female")} className="text-xs font-medium text-pink-600 hover:underline" data-testid="gender-all-female">All Madam</button>
            <span className="text-slate-300">·</span>
            <button type="button" onClick={() => setAll("male")} className="text-xs font-medium text-sky-600 hover:underline" data-testid="gender-all-male">All Sir</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 divide-y divide-slate-100 dark:divide-slate-800">
          {staff.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 py-2" data-testid={`gender-row-${t.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{t.name}</div>
                <div className="text-[11px] text-slate-400 truncate">{t.phone}</div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {OPTIONS.map((o) => {
                  const on = (draft[t.id] || "") === o.value;
                  const Icon = o.icon;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setOne(t.id, o.value)}
                      data-testid={`gender-${t.id}-${o.value}`}
                      className={`h-8 px-3 rounded-full text-xs font-medium border inline-flex items-center gap-1 transition-colors ${
                        on ? o.on : `bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-300 ${o.cls} dark:hover:bg-slate-700`
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" /> {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {!staff.length && <div className="text-sm text-slate-400 py-6 text-center">No teachers to update.</div>}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="gender-bulk-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="gender-bulk-save">
            {saving ? "Saving…" : pending.length ? `Save ${pending.length} change${pending.length === 1 ? "" : "s"}` : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
